import {
  Box,
  OutlinedInput,
  Button,
  Typography,
  Grid,
  IconButton,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import Layout from "../../components/layout";
import salonService from "../../services/salonServices";
import { DecryptData, EncryptData, jsonParser } from "../../helpers/helper";
import { toast } from "react-toastify";

import {
  AddCircleOutline as AddIcon,
  DeleteSweep as DeleteIcon,
  InfoOutlined as InfoIcon,
  MonetizationOnOutlined as PriceIcon,
} from "@mui/icons-material";
import ValidationModal from "../BootstrapValidationModal";
import LinearProgress from '@mui/material/LinearProgress';

export default function AddNewServices() {
  const history = useHistory();

  // Existing States
  const [progress, setProgress] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingModalOpen, setIsSubmittingModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceType, setServiceType] = useState(null);
  const [serviceTypeError, setServiceTypeError] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const [serviceNameError, setServiceNameError] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState(false);
  const [serviceTypeData, setServiceTypeData] = useState([]);
  const [newServiceType, setNewServiceType] = useState("");
  const [price, setPrice] = useState("");
  const [priceError, setPriceError] = useState(false);

  // 🎯 Toggle for variations
  const [hasVariations, setHasVariations] = useState(false);

  // 🎯 Variations State
  // Structure: [{ typeName: 'Length', options: [{ name: 'Shoulder', price: '5000' }] }]
  const [variations, setVariations] = useState([]);

  // 🎯 Calculated Max Price for UI Preview
  const maxPrice =
    variations.length > 0
      ? Math.max(
          ...variations.flatMap((v) =>
            v.options.map((o) => Number(o.price) || 0)
          )
        )
      : 0;

  useEffect(() => {
    const fetchServiceType = async () => {
      const service = await salonService.serviceType();
      const decryptData = await DecryptData(service);
      const parsedData = jsonParser(decryptData);
      parsedData.map((data) => {
        data.serviceName = DecryptData(data.serviceName);
      });
      setServiceTypeData(parsedData);
    };
    fetchServiceType();
  }, []);

  // Progress Bar Logic (Kept Original)
  useEffect(() => {
    let intervalId;
    let isMounted = true;

    const startProgress = () => {
      intervalId = setInterval(() => {
        setProgress((oldProgress) => {
          const newProgress = oldProgress + 100 / steps;
          if (newProgress >= 100) {
            clearInterval(intervalId);
            setTimeout(() => {
              if (isMounted) {
                setIsSubmittingModalOpen(false);
                history.push(`/services`);
              }
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, intervalDuration);
    };

    const intervalDuration = 30;
    const totalTime = 1000;
    const steps = totalTime / intervalDuration;

    if (isSubmittingModalOpen) {
      startProgress();
    }

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isSubmittingModalOpen]);

  const closeSubmittingModal = () => {
    setIsSubmittingModalOpen(false);
  };

  // 🎯 Variation Handlers
  const addVariationType = () => {
    setVariations([
      ...variations,
      {
        linkedId: null,
        typeName: "",
        options: [{ name: "", price: "" }],
      },
    ]);
  };

  const removeVariationType = (index) => {
    const newVars = [...variations];
    newVars.splice(index, 1);
    setVariations(newVars);
  };

  const addOption = (typeIndex) => {
    const newVars = [...variations];
    newVars[typeIndex].options.push({ name: "", price: "" });
    setVariations(newVars);
  };

  const removeOption = (typeIndex, optIndex) => {
    const newVars = [...variations];
    newVars[typeIndex].options.splice(optIndex, 1);
    setVariations(newVars);
  };

  const handleVariationChange = (typeIndex, optIndex, field, value) => {
    const newVars = [...variations];

    if (field === "linkedId") {
      console.log(typeIndex + "," + value);
      newVars[typeIndex].linkedId = value;
      newVars[value].linkedId = typeIndex;
      return;
    }
    if (field === "typeName") {
      newVars[typeIndex].typeName = value;
    } else {
      if (field === "price") value = value.replace(/[^0-9]/g, "");
      newVars[typeIndex].options[optIndex][field] = value;
    }
    setVariations(newVars);
  };

  const handlePriceChange = (e) => {
    let { value } = e.target;
    value = value.replace(/[^0-9]/g, "");
    setPrice(value);
    if (value !== "") {
      setPriceError(false);
    }
  };

  const onSubmit = async () => {
    let error = 0;
    if (serviceType === null) {
      setServiceTypeError(true);
      error++;
    }
    if (serviceName === "") {
      setServiceNameError(true);
      error++;
    }

    // 🎯 Validate based on hasVariations
    if (!hasVariations && price === "") {
      setPriceError(true);
      error++;
    }

    if (error > 0) {
      setIsModalOpen(true);
      return;
    }

    try {
      const payload = {
        serviceName: EncryptData(serviceName),
        description: EncryptData(description),
        serviceTypeId: serviceType === "new" ? null : Number(serviceType),
        serviceTypeName: newServiceType,
        // 🎯 Conditional variations logic
        variations: hasVariations
          ? variations.map((v) => ({
              id: v.id,
              linkedId: v.linkedId,
              name: EncryptData(v.typeName),
              options: v.options.map((o) => ({
                name: EncryptData(o.name),
                price: EncryptData(o.price.toString()),
              })),
            }))
          : [],
        finalMaxPrice: EncryptData(hasVariations ? maxPrice.toString() : price),
      };

      await salonService.save(payload);
      setIsSubmittingModalOpen(true);
    } catch (err) {
      setIsSubmitting(false);
      toast.error("Failed to save service");
    }
  };

  const handleChangeDescription = (e) => {
    let { value } = e.target;

    value = value.replace(/[^a-zA-Z0-9 ]/g, "");
    setDescription(value);
    if (value !== "") {
      setDescriptionError(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header Section */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" className="page-main-title">
              Create New Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasVariations
                ? "Define service details with tiered pricing variations."
                : "Define service details with a fixed price."}
            </Typography>
          </Box>
          <button
            className="btn-save-top"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Service"}
          </button>
        </Box>

        <Grid container spacing={4}>
          {/* LEFT COLUMN: Service Identity */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} className="info-card-container">
              <Typography variant="h6" className="section-subtitle">
                General Details
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <div className="form-field">
                <label>Service Category</label>
                <select
                  className={`modern-input ${serviceTypeError ? "error" : ""}`}
                  onChange={(e) => setServiceType(e.target.value)}
                  value={serviceType || ""}
                >
                  <option value="">Select Category</option>
                  {serviceTypeData.map((row, index) => (
                    <option key={index} value={row.id}>
                      {row.serviceName}
                    </option>
                  ))}
                  <option value="new">+ Create New Category</option>
                </select>
              </div>

              {serviceType === "new" && (
                <div className="form-field">
                  <label>New Category Name</label>
                  <OutlinedInput
                    fullWidth
                    className="modern-text-input"
                    value={newServiceType}
                    onChange={(e) => setNewServiceType(e.target.value)}
                  />
                </div>
              )}

              <div className="form-field">
                <label>Service Name</label>
                <OutlinedInput
                  fullWidth
                  className="modern-text-input"
                  error={serviceNameError}
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="e.g. Master Color Treatment"
                />
              </div>

              <div className="form-field">
                <label>Description</label>
                <textarea
                  className={`modern-textarea ${
                    descriptionError ? "error" : ""
                  }`}
                  onChange={handleChangeDescription}
                  value={description}
                  rows={6}
                  placeholder="Briefly describe what this service includes..."
                />
              </div>

              {/* 🎯 Variation Toggle */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #eee" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasVariations}
                      onChange={(e) => {
                        setHasVariations(e.target.checked);
                        // Reset variations when toggling off
                        if (!e.target.checked) {
                          setVariations([]);
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        This service has variations
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enable if service has different options (e.g., sizes,
                        lengths)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT COLUMN: Pricing Section */}
          <Grid item xs={12} lg={8}>
            {!hasVariations ? (
              // 🎯 Simple Pricing (No Variations)
              <Paper elevation={0} className="info-card-container">
                <Typography variant="h6" className="section-subtitle">
                  Service Price
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ p: 3, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Enter the fixed price for this service
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                    <Box sx={{ flex: 1 }}>
                      <label style={{ display: "block", marginBottom: 8 }}>
                        Price
                      </label>
                      <OutlinedInput
                        fullWidth
                        type="text"
                        placeholder="Enter price"
                        value={price}
                        onChange={handlePriceChange}
                        error={priceError}
                        startAdornment={
                          <Typography sx={{ mr: 1, fontSize: 14 }}>
                            Rs
                          </Typography>
                        }
                      />
                      {priceError && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Price is required
                        </Typography>
                      )}
                    </Box>
                    {price && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#719879",
                          color: "white",
                          borderRadius: 1,
                          textAlign: "center",
                          minWidth: 120,
                        }}
                      >
                        <Typography variant="caption">Final Price</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Rs. {Number(price).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            ) : (
              // 🎯 Variations Pricing
              <Paper elevation={0} className="info-card-container">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6" className="section-subtitle">
                      Pricing & Variations
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The final price will be the{" "}
                      <strong style={{ color: "#719879" }}>
                        highest value
                      </strong>{" "}
                      among selected variations.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addVariationType}
                    className="btn-add-category"
                  >
                    Add Variation Group
                  </Button>
                </Box>

                <div className="variation-grid">
                  {variations.length === 0 ? (
                    <Box className="empty-variation-state">
                      <PriceIcon sx={{ fontSize: 40, mb: 1, color: "#ccc" }} />
                      <Typography variant="body2">
                        No price variations defined yet.
                      </Typography>
                    </Box>
                  ) : (
                    variations
                      .map((v, tIdx) => ({ ...v, originalIndex: tIdx }))
                      // Only render the card if it's NOT linked to something else
                      .map((parentVar) => (
                        <div
                          key={parentVar.originalIndex}
                          className="variation-module"
                          style={{
                            border: "1px solid #719879",
                            overflow: "hidden",
                          }}
                        >
                          {/* PARENT HEADER */}
                          <Box
                            className="variation-header"
                            sx={{ bgcolor: "#f0f7f1", p: 1.5 }}
                          >
                            <OutlinedInput
                              size="small"
                              placeholder="Variation Name (e.g., Size)"
                              value={parentVar.typeName}
                              onChange={(e) =>
                                handleVariationChange(
                                  parentVar.originalIndex,
                                  null,
                                  "typeName",
                                  e.target.value
                                )
                              }
                              sx={{
                                flexGrow: 1,
                                fontWeight: 700,
                                bgcolor: "white",
                              }}
                            />
                            <Box sx={{ ml: 2, display: "flex", gap: 1 }}>
                              <Select
                                size="small"
                                displayEmpty
                                value={parentVar.linkedId ?? ""}
                                renderValue={(selected) => {
                                  if (selected === "") return "Link Another...";

                                  const linkedVar = variations[selected];
                                  return (
                                    linkedVar?.typeName ||
                                    `Variation ${selected + 1}`
                                  );
                                }}
                                onChange={(e) =>
                                  handleVariationChange(
                                    parentVar.originalIndex,
                                    null,
                                    "linkedId",
                                    e.target.value
                                  )
                                }
                                sx={{
                                  width: 160,
                                  height: 35,
                                  fontSize: "12px",
                                  bgcolor: "white",
                                }}
                              >
                                <MenuItem disabled value="">
                                  Link Another...
                                </MenuItem>
                                {variations.map(
                                  (v, idx) =>
                                    idx !== parentVar.originalIndex &&
                                    !v.linkedId && (
                                      <MenuItem key={idx} value={idx}>
                                        Link:{" "}
                                        {v.typeName || `Variation ${idx + 1}`}
                                      </MenuItem>
                                    )
                                )}
                              </Select>
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeVariationType(parentVar.originalIndex)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>

                          <Box sx={{ p: 2 }}>
                            {/* PARENT OPTIONS */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                color: "#719879",
                                textTransform: "uppercase",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Primary Options
                            </Typography>
                            {parentVar.options.map((opt, oIdx) => (
                              <div
                                key={oIdx}
                                className="option-row"
                                style={{ marginBottom: "8px" }}
                              >
                                <OutlinedInput
                                  size="small"
                                  placeholder="Option"
                                  value={opt.name}
                                  onChange={(e) =>
                                    handleVariationChange(
                                      parentVar.originalIndex,
                                      oIdx,
                                      "name",
                                      e.target.value
                                    )
                                  }
                                  sx={{ flex: 2 }}
                                />
                                <OutlinedInput
                                  size="small"
                                  placeholder="Price"
                                  value={opt.price}
                                  onChange={(e) =>
                                    handleVariationChange(
                                      parentVar.originalIndex,
                                      oIdx,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  sx={{ flex: 1 }}
                                  startAdornment={
                                    <Typography sx={{ mr: 1, fontSize: 12 }}>
                                      Rs
                                    </Typography>
                                  }
                                />
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    removeOption(parentVar.originalIndex, oIdx)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </div>
                            ))}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addOption(parentVar.originalIndex)}
                              sx={{ color: "#719879", mb: 2 }}
                            >
                              Add Option
                            </Button>

                            {/* NESTED LINKED VARIATIONS (Inside the same card) */}
                            {variations
                              .map((v, idx) => ({ ...v, originalIndex: idx }))
                              .filter(
                                (child) =>
                                  child.linkedId === parentVar.typeName &&
                                  parentVar.typeName !== ""
                              )
                              .map((childVar) => (
                                <Box
                                  key={childVar.originalIndex}
                                  sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: "2px dashed #e0e0e0",
                                    pl: 3,
                                    position: "relative",
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: 0,
                                      top: 20,
                                      bottom: 20,
                                      width: "4px",
                                      backgroundColor: "#719879",
                                      borderRadius: "4px",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, color: "#444" }}
                                    >
                                      Linked:{" "}
                                      {childVar.typeName ||
                                        "Untitled Variation"}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="text"
                                      color="error"
                                      onClick={() =>
                                        handleVariationChange(
                                          childVar.originalIndex,
                                          null,
                                          "linkedId",
                                          null
                                        )
                                      }
                                      sx={{ fontSize: "10px" }}
                                    >
                                      Unlink
                                    </Button>
                                  </Box>

                                  {childVar.options.map((cOpt, coIdx) => (
                                    <div
                                      key={coIdx}
                                      className="option-row"
                                      style={{ marginBottom: "8px" }}
                                    >
                                      <OutlinedInput
                                        size="small"
                                        placeholder="Linked Option"
                                        value={cOpt.name}
                                        onChange={(e) =>
                                          handleVariationChange(
                                            childVar.originalIndex,
                                            coIdx,
                                            "name",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 2, bgcolor: "#fdfdfd" }}
                                      />
                                      <OutlinedInput
                                        size="small"
                                        placeholder="Price"
                                        value={cOpt.price}
                                        onChange={(e) =>
                                          handleVariationChange(
                                            childVar.originalIndex,
                                            coIdx,
                                            "price",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 1, bgcolor: "#fdfdfd" }}
                                        startAdornment={
                                          <Typography
                                            sx={{ mr: 1, fontSize: 12 }}
                                          >
                                            Rs
                                          </Typography>
                                        }
                                      />
                                    </div>
                                  ))}
                                </Box>
                              ))}
                          </Box>
                        </div>
                      ))
                  )}
                </div>

                {/* Price Rule Summary Footer */}
                {maxPrice > 0 && (
                  <Box className="logic-summary-footer">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InfoIcon color="success" fontSize="small" />
                      <Typography variant="body2">
                        Automatic Pricing Rule Active
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="caption">
                        Maximum Service Value
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#719879" }}
                      >
                        Rs. {maxPrice.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      <ValidationModal
        isOpen={isSubmittingModalOpen}
        onClose={closeSubmittingModal}
      >
        <div className="modal__popup">
          <div className="Portal-Login-Form">
            <div className="ProgressModal">
              <Box className="progressOfSubmitting" sx={{ width: "100%" }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 15,
                    borderRadius: 10,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#1fc214",
                    },
                  }}
                />
              </Box>
              <span>Submitting Request</span>
            </div>
          </div>
        </div>
      </ValidationModal>
    </Layout>
  );
}
